import { memo, useCallback } from "react";
import { useLocalization } from "@fluent/react";
import cc from "classcat";

import { modules } from "byzantine";
import SortableItem from "../../../../src/components/dragAndDrop/SortableItem";
import RowDragIndicator from "./RowDragIndicator";
import styles from "./DashboardCustomization.module.scss";
import SECTION_LABELS_TO_FLUENT_IDS, {
  SECTION_LABELS_TO_DESCRIPTION_FLUENT_IDS,
  SECTION_LABELS_TO_ICONS
} from "../constants";

type SectionRowProps = {
  id: string;
  isHalf?: boolean;
  isPreview?: boolean;
};

const DashboardSectionRow = ({ id, isPreview, isHalf }: SectionRowProps) => {
  const { l10n } = useLocalization();
  const isDashboardLayoutRedesignEnabled =
    modules.dashboard.useDashboardLayoutRedesignEnabled();
  // If the section is a Narmi Tile client id we'll grab the relevant data
  const tile = modules.narmiTiles.useSectionTile(id);
  const labelFtlId = SECTION_LABELS_TO_FLUENT_IDS[id];
  const sectionLabel = labelFtlId ? l10n.getString(labelFtlId, null) : id;
  const label = tile ? tile.name : sectionLabel;
  const descriptionFtlId = SECTION_LABELS_TO_DESCRIPTION_FLUENT_IDS[id];
  const sectionDescription = descriptionFtlId
    ? l10n.getString(descriptionFtlId, null)
    : id;
  const description = tile
    ? tile.tagline || tile.description
    : sectionDescription;
  const icon = tile ? tile.image : SECTION_LABELS_TO_ICONS[id];
  const isBlank = id === "blank";
  const getClassNames = useCallback(
    (isDragging: boolean) => {
      if (isHalf) {
        return cc([
          styles["row-half"],
          isDragging && styles["row-dragging"],
          isPreview && styles["row-half-preview"],
          isBlank && styles["row-half--blank"]
        ]);
      }
      return cc([
        styles.row,
        !isDashboardLayoutRedesignEnabled && styles["row-padding"],
        isDragging && styles["row-dragging"]
      ]);
    },
    [isHalf, isPreview]
  );

  return (
    <SortableItem id={id}>
      {({ setNodeRef, attributes, listeners, style, isDragging }) => (
        <div
          ref={setNodeRef}
          style={style}
          className={getClassNames(isDragging)}
          {...attributes}
          {...listeners}
        >
          {isDashboardLayoutRedesignEnabled ? (
            <div className={styles["row-info"]}>
              <div className={styles["icon-container"]}>
                <span
                  className={cc([`narmi-icon-${icon}`, styles["icon-color"]])}
                />
              </div>

              <div className={styles["heading-container"]}>
                {label}
                <span className={styles.subheading}>{description}</span>
              </div>
            </div>
          ) : (
            label
          )}
          {(!isDashboardLayoutRedesignEnabled || isHalf) && (
            <span className={cc(["narmi-icon-menu", styles["icon-color"]])} />
          )}
          {isDashboardLayoutRedesignEnabled && !isHalf && !isPreview && (
            <RowDragIndicator />
          )}
        </div>
      )}
    </SortableItem>
  );
};

export default memo(DashboardSectionRow);
